import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { FormControlLabel, Switch, TextField } from '@material-ui/core'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import Editor from '../../../components/editor/Editor'
import { configuracionLegal } from '../../../components/editor/configuracionLegal'
import { getLegal, updateLegal } from '../../../../api/legal'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'

/**
 * Editar un texto legal.
 *
 * El documento se identifica por LO QUE ES —clave e idioma— y no por un id:
 * por eso la dirección es `/edit-legal/privacidad/es`. Ninguna de las dos se
 * puede cambiar desde aquí; cambiarlas no sería editar el documento, sería
 * convertirlo en otro y dejar un hueco donde la web espera encontrar algo.
 *
 * PUBLICAR ES UNA DECISIÓN, NO UN GUARDADO. Se puede guardar cuantas veces
 * haga falta sin que lo vea nadie; el interruptor de publicar pide
 * confirmación porque a partir de ahí el texto es lo que el sitio dice de sí
 * mismo ante la ley.
 */

const IDIOMAS = { es: 'castellano', ca: 'català', en: 'English' }

export default function EditLegalPage() {
	const { clave, idioma } = useParams()
	const history = useHistory()

	const [documento, setDocumento] = useState(null)
	const [titulo, setTitulo] = useState('')
	const [contenido, setContenido] = useState('')
	const [guardando, setGuardando] = useState(false)
	const [confirmar, setConfirmar] = useState(false)
	// Sube cada vez que el texto llega del servidor. Es la llave del editor: ver
	// el comentario de abajo, en <Editor>.
	const [cargas, setCargas] = useState(0)

	useEffect(() => {
		getLegal(clave, idioma)
			.then((res) => {
				if (res.status !== 200)
					return
				/**
				 * EL ORDEN IMPORTA, y esto costó una pantalla en blanco.
				 *
				 * `documento` es lo que abre la pantalla, así que va EL ÚLTIMO:
				 * con React 17 las actualizaciones dentro de una promesa no se
				 * agrupan, y poniéndolo primero el editor se montaba en ese
				 * render —con el texto todavía vacío— y el contenido llegaba
				 * después, cuando ya era tarde.
				 */
				setContenido(res.data.contenido ?? '')
				setTitulo(res.data.titulo ?? '')
				setCargas((n) => n + 1)
				setDocumento(res.data)
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudo cargar el documento.' }))
	}, [clave, idioma])

	/**
	 * Guarda y se queda con lo que devuelva el servidor.
	 *
	 * Es importante que se quede con ESO y no con lo que hay en pantalla: el
	 * servidor sanea el HTML al entrar, así que lo guardado puede no ser
	 * exactamente lo que se escribió. Verlo al momento evita descubrirlo tres
	 * ediciones después.
	 */
	function guardar(publicado) {
		setGuardando(true)
		return updateLegal(clave, idioma, { titulo, contenido, ...(publicado === undefined ? {} : { publicado }) })
			.then((res) => {
				setContenido(res.data.contenido ?? '')
				setTitulo(res.data.titulo ?? '')
				// Otra carga: el editor se vuelve a montar con lo que haya
				// guardado el servidor, que puede no ser letra por letra lo que
				// se escribió — el HTML se sanea al entrar.
				setCargas((n) => n + 1)
				setDocumento(res.data)
				alertSuccess({
					title: 'Guardado',
					customMessage:
						res.data.publicado
							? 'Publicado: ya se ve en sailradar.com.'
							: 'Guardado en borrador. Todavía no lo ve nadie.',
				})
			})
			.catch((error) => alertError({ error, customMessage: 'No se pudo guardar.' }))
			.finally(() => setGuardando(false))
	}

	if (!documento)
		return (
			<Card>
				<CardHeader title="Texto legal" />
				<CardBody>Cargando…</CardBody>
			</Card>
		)

	const vacio = !contenido.replace(/<[^>]+>/g, '').trim()
	const huecos = (contenido.match(/「[^」]*」/g) ?? []).length

	return (
		<Card>
			<CardHeader title={`${documento.nombre ?? clave} · ${IDIOMAS[idioma] ?? idioma}`}>
				<CardHeaderToolbar>
					<button type="button" className="btn btn-light mr-2" onClick={() => history.push('/legal')}>
						Volver
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={guardando}
						onClick={() => guardar(undefined)}
					>
						Guardar
					</button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				{/* Los huecos del borrador, contados. Publicar un texto con un
				    「NIF」 dentro es exactamente lo que hay que evitar. */}
				{huecos > 0 && (
					<div className="alert alert-warning">
						Quedan <strong>{huecos}</strong> huecos entre 「 」 por rellenar. Búscalos en el texto: son
						los datos que faltan (razón social, NIF, domicilio…). No publiques con ellos dentro.
					</div>
				)}

				<TextField
					label="Título de la página"
					helperText="Es el titular que se ve en la web y el nombre del enlace en el pie."
					value={titulo}
					onChange={(e) => setTitulo(e.target.value)}
					margin="normal"
					fullWidth
				/>

				<div style={{ marginTop: 20, marginBottom: 8, color: '#7b8794' }}>
					El aspecto —tipografía, tamaños, márgenes— lo pone la web. Aquí se decide qué DICE y cómo se
					estructura: apartados, listas, enlaces y tablas.
				</div>

				{/*
				  LA LLAVE NO ES DECORATIVA: sin ella la pantalla salía en blanco.

				  CKEditor arranca de forma asíncrona, y su envoltorio de React
				  descarta cualquier cambio de `data` que llegue ANTES de que el
				  editor exista —mira `this.editor` y, si todavía es nulo, se lo
				  traga sin decir nada—. El texto se pierde y no vuelve.

				  Con la llave, cada carga monta un editor nuevo que nace ya con
				  su contenido, en vez de fiarlo a que gane una carrera.
				*/}
				<Editor
					key={`${clave}|${idioma}|${cargas}`}
					body={contenido}
					setBody={setContenido}
					config={configuracionLegal}
					lang="es"
					placeholder="El texto del documento…"
				/>

				<div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #ebedf3' }}>
					<FormControlLabel
						control={
							<Switch
								checked={Boolean(documento.publicado)}
								disabled={guardando || (!documento.publicado && (vacio || !titulo.trim()))}
								onChange={() => {
									// Publicar pregunta; despublicar no. Quitar algo del
									// público siempre se puede deshacer volviendo a
									// publicar, y a veces hay prisa por quitarlo.
									if (!documento.publicado)
										setConfirmar(true)
									else
										guardar(false)
								}}
							/>
						}
						label={
							documento.publicado
								? 'Publicado: se ve en sailradar.com'
								: 'En borrador: su dirección contesta como si no existiera'
						}
					/>
				</div>

				<ConfirmDialog
					title="Publicar este texto"
					open={confirmar}
					setOpen={setConfirmar}
					onConfirm={() => guardar(true)}
				>
					<p>
						A partir de ahora será lo que SailRadar dice de sí misma ante la ley, y estará enlazado
						desde el pie de todas las páginas.
					</p>
					<p style={{ color: '#7b8794' }}>
						¿Lo ha revisado alguien que sepa? {huecos > 0 && (
							<strong style={{ color: '#c0392b' }}>Todavía quedan {huecos} huecos por rellenar.</strong>
						)}
					</p>
				</ConfirmDialog>
			</CardBody>
		</Card>
	)
}
